import React, { useState, useEffect, useRef } from "react";
import { BrandButton as Button, RolesSelector } from "@/components";
import HospiceSearchableSelect from "@/components/HospiceSearchableSelect";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuthenticatedApi } from "@/utils/callApi";
import SafeLocalStorage from "@/utils/safeLocalStorage";

export default function CreateUser() {
  
  // API integration using callApi utility
  const { secureHelpers, isLoading: apiLoading, error: apiError } = useAuthenticatedApi();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Multi-hospice assignment state
  const [assignments, setAssignments] = useState([]); // [{hospiceId, hospiceName, roleIds}]
  const [newHospiceId, setNewHospiceId] = useState("");
  const [newRoleIds, setNewRoleIds] = useState([]);

  const [toast, setToast] = useState(null);
  // Ref for scrolling to toast
  const toastRef = useRef(null);
  
  // HOSPICES STATE FROM API
  const [hospices, setHospices] = useState([]);
  const [hospicesLoading, setHospicesLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Load users and hospices from API on mount
  useEffect(() => {
    loadHospices();
  }, []);
  
  const loadHospices = async () => {
    setHospicesLoading(true);
    try {
      if (import.meta.env.DEV) {
        console.log('Loading hospices from /api/Hospices...');
      }
      const hospicesList = await secureHelpers.get('/api/Hospices');
      if (import.meta.env.DEV) {
        console.log('Hospices loaded:', hospicesList);
      }
      
      if (Array.isArray(hospicesList)) {
        const sortedHospices = hospicesList.sort((a, b) => a.name.localeCompare(b.name));
        setHospices(sortedHospices);
      } else {
        if (import.meta.env.DEV) {
          console.warn('API returned non-array hospices:', hospicesList);
        }
        setHospices([]);
      }
    } catch (err) {
      console.error('Failed to load hospices:', err);
      setError('Unable to load hospices. Please try again or contact support on clinical@hospiceuk.org.');
      setHospices([]);
    } finally {
      setHospicesLoading(false);
    }
  };

   const loadUsers = async () => {
        try {
            const usersData = await secureHelpers.get('/api/Members');
            setUsers(usersData);
        } catch (err) {
            console.error("Failed to load users:", err);
        }
    };

  // keyboard + mouse navigable suggestions

  // memoised filtered list for suggestions (limit to 10)
  // Updated to match API field names: firstName, lastName, hospiceId
  
  // reset highlight when query changes

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  // Add new hospice-role assignment
  const addAssignment = () => {
    if (!newHospiceId || newRoleIds.length === 0) return;
    
    const hospice = hospices.find(h => h.id === newHospiceId);
    const newAssignment = {
      hospiceId: newHospiceId,
      hospiceName: hospice?.name || newHospiceId,
      roleIds: newRoleIds
    };
    
    setAssignments([...assignments, newAssignment]);
    setNewHospiceId("");
    setNewRoleIds([]);
  };

  // Remove hospice-role assignment
  const removeAssignment = (hospiceId) => {
    setAssignments(assignments.filter(a => a.hospiceId !== hospiceId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError("First name, last name, and email are required.");
      return;
    }
    
    if (assignments.length === 0) {
      setError("At least one hospice-role assignment is required.");
      return;
    }
    
    setSaving(true);
    try {
      // Flatten assignments to HospiceRoles format
      const hospiceRoles = assignments.flatMap(assignment =>
        assignment.roleIds.map(roleId => ({
          hospiceId: assignment.hospiceId,
          roleId: roleId
        }))
      );

      const memberData = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        hospiceRoles: hospiceRoles
      };
      
      const newUser = await secureHelpers.post('/api/Members', memberData);
      
      await loadUsers();
      
      setForm({ firstName: "", lastName: "", email: "" });
      setAssignments([]);
      setNewHospiceId("");
      setNewRoleIds([]);
      setToast({ type: "success", text: `User ${newUser.firstName} ${newUser.lastName} created successfully.` });
      
      // Scroll to top to show toast
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toastRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      console.error(err);
      setError('Unable to create user. Please try again or contact support on clinical@hospiceuk.org.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4">
      {toast && (
        <div 
          ref={toastRef}
          className={`alert alert-${toast.type} alert-dismissible`} 
          role="alert" 
          style={{ 
            maxWidth: 800, 
            position: 'sticky',
            top: '1rem',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            fontSize: '1.1rem'
          }}
        >
          {toast.text}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setToast(null)} />
        </div>
      )}

      <h2 style={{ color: "#F16D09", marginTop: "1rem" }}>Create User</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit} style={{ maxWidth: 800 }}>
        <div className="mb-3">
          <label className="form-label">First Name</label>
          <input name="firstName" className="form-control" value={form.firstName} onChange={handleChange} required />
        </div>

        <div className="mb-3">
          <label className="form-label">Last Name</label>
          <input name="lastName" className="form-control" value={form.lastName} onChange={handleChange} required />
        </div>

        <div className="mb-3">
          <label className="form-label">Email</label>
          <input name="email" type="email" className="form-control" value={form.email} onChange={handleChange} required />
        </div>

        {/* Current Assignments */}
        <div className="mb-4">
          <h5 className=" mb-3">Hospice-Role Assignments</h5>
          {assignments.length === 0 ? (
            <p className="text-muted">No assignments yet. Add at least one below.</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {assignments.map((assignment) => (
                <div 
                  key={assignment.hospiceId}
                  className="card p-3"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0" style={{ color: '#F16D09' }}>{assignment.hospiceName}</h6>
                    <Button
                      onClick={() => removeAssignment(assignment.hospiceId)}
                      style={{ background: '#dc3545', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                    >
                      ×
                    </Button>
                  </div>
                  <ul className="mb-0" style={{ paddingLeft: '1.25rem' }}>
                    {assignment.roleIds.map(roleId => (
                      <li key={roleId} style={{ color: '#495057' }}>
                        {roleId}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Assignment */}
        <div className="mb-4 card p-3" style={{ backgroundColor: '#e7f3ff' }}>
          <h5 className=" mb-3">Add Hospice-Role Assignment</h5>
          
          <div className="mb-3">
            <label className="form-label ">Select Hospice</label>
            <HospiceSearchableSelect
              hospices={hospices.filter(h => !assignments.some(a => a.hospiceId === h.id))}
              value={newHospiceId}
              onChange={(value) => setNewHospiceId(value)}
              placeholder={hospicesLoading ? 'Loading hospices...' : 'Search or select...'}
              disabled={hospicesLoading}
            />
            {hospicesLoading && (
              <small className="text-muted d-flex align-items-center gap-2 mt-1">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Loading hospices...
              </small>
            )}
          </div>

          {newHospiceId && (
            <div className="mb-3">
              <RolesSelector
                selectedRoles={newRoleIds}
                onRolesChange={setNewRoleIds}
                title="Select Roles"
                description="Choose roles for this hospice"
                required={false}
              />
            </div>
          )}

          <Button
            onClick={addAssignment}
            disabled={!newHospiceId || newRoleIds.length === 0 || hospicesLoading}
            style={{ background: '#f16d09' }}
          >
            Add Assignment
          </Button>
        </div>

        <div className="d-flex gap-3">
          <Button type="submit" disabled={saving || apiLoading}>{saving ? "Saving…" : "Create User"}</Button>
          <Button type="button" onClick={() => {
            setForm({ firstName: "", lastName: "", email: "" });
            setAssignments([]);
            setNewHospiceId("");
            setNewRoleIds([]);
            setToast(null);
            setError("");
          }} variant="secondary">
            Reset
          </Button>
        </div>
      </form>
    </div>
  );
}
